import { compose, withProps, hoistStatics } from "recompose";
import DispatcherQuery from "./DispatcherQuery.gql";
import { withRouter } from "react-router";
import graphqlWithPreload from "web/core/apollo/graphqlWithPreload";
import Loadable from "theme/components/helpers/Loadable";
import dispatchRoutes from "./dispatchRoutes";
import withEntityNotFound from "theme/modules/PageError/NotFound/withEntityNotFound";
import withErrorFallback from "theme/modules/PageError/withErrorFallback";
import LoadingRoute from "theme/modules/Router/LoadingRoute";

const makeVariables = (params) => {
  return Object.values(dispatchRoutes)
    .filter(({ variables }) => typeof variables === "function")
    .reduce(
      (acc, { variables }) => ({
        ...acc,
        ...variables(params),
      }),
      {
        url: params.location.pathname,
      }
    );
};

const getElementFromProps = (props) => {
  if (
    Object.prototype.hasOwnProperty.call(
      dispatchRoutes,
      props.matched.__typename
    )
  ) {
    return dispatchRoutes[props.matched.__typename].render(props);
  }

  console.error(
    `Please handle the type ${props.matched.__typename} in the dispatcher`
  );

  return null;
};

const Dispatcher = (props) => getElementFromProps(props);

const getMatchedRouteFromData = (data) => {
  return data && data.route;
};

const withFetchedMatchedUrls = compose(
  hoistStatics(withRouter),
  graphqlWithPreload(DispatcherQuery, {
    options: (props) => {
      return {
        variables: makeVariables(props),
      };
    },
    preloadOptions: (params) => {
      return {
        variables: makeVariables(params),
      };
    },
  }),
  withEntityNotFound({
    isFound: (props) =>
      props.data.error ||
      props.data.loading ||
      getMatchedRouteFromData(props.data),
  }),
  withErrorFallback({
    hasError: (props) => props.data.error,
  }),
  Loadable((props) => {
    const url =
      getMatchedRouteFromData(props.data) &&
      (getMatchedRouteFromData(props.data).path ||
        getMatchedRouteFromData(props.data).url);

    return (
      getMatchedRouteFromData(props.data) &&
      props.location.pathname === url.replace(/^[^/]/, (match) => "/" + match)
    );
  }, LoadingRoute),
  withProps((props) => ({
    loading: props.data.loading,
    matched: getMatchedRouteFromData(props.data),
  }))
);

const EnhancedDispatcher = withFetchedMatchedUrls(Dispatcher);

const preloadDispatcherQuery = EnhancedDispatcher.preload;
EnhancedDispatcher.preload = (params) => {
  return preloadDispatcherQuery(params).then((result) => {
    const matched = result && getMatchedRouteFromData(result.data);
    if (!matched) {
      return;
    }

    const element = getElementFromProps({ matched });
    if (!element || !element.type.load) {
      return;
    }

    return element.type.load();
  });
};

export default EnhancedDispatcher;
